import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import HeadModel from './components/HeadModel';
import NavBar from './components/NavBar';
import Hero from './components/Hero';
import AboutSection from './components/AboutSection';
import HowToBuy from './components/HowToBuy';
import Roadmap from './components/RoadMap';
import Team from './components/Team';
import Footer from './components/Footer';
import WiggaPaper from './components/WiggaPaper'; // Import the WiggaPaper component
import './App.css';
import './components/EntryScreen.css';
import './components/WiggaPaper.css'; // Import the WiggaPaper CSS

const App = () => {
  // === ENTRY SCREEN STATE ===
  const [scanProgress, setScanProgress] = useState(0);
  const [scanActive, setScanActive] = useState(false);
  const [memeIndex, setMemeIndex] = useState(0);
  const [memeText, setMemeText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [entryComplete, setEntryComplete] = useState(false);
  
  // === MAIN APP STATE ===
  const [showMainContent, setShowMainContent] = useState(false);
  const [backgroundVideoLoaded, setBackgroundVideoLoaded] = useState(false);
  
  // === NAVIGATION STATE ===
  const [currentPage, setCurrentPage] = useState('main'); // 'main' or 'wiggapaper'
  
  // === AUDIO STATE ===
  const [accessGrantedPlayed, setAccessGrantedPlayed] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  
  // === AUDIO REFS ===
  const scanSoundRef = useRef(null);
  const accessGrantedSoundRef = useRef(null);
  const backgroundMusicRef = useRef(null);
  const backgroundVideoRef = useRef(null);
  
  // === ANIMATION REFS ===
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const typewriterRef = useRef(null);
  const completionTimerRef = useRef(null);
  const backgroundMusicTimerRef = useRef(null);
  
  // === CONSTANTS ===
  const SCAN_DURATION = 10000; // 10 seconds for full scan
  const TYPING_SPEED = 30; // milliseconds per character
  
  // === MUSIC TRACKS ===
  const musicTracks = [
    { title: "WiggaChain", file: "wiggachain.mp3" },
    { title: "Crypto Hustle", file: "Level Up!.mp3" },
    { title: "Diamond Hands", file: "Chain Gang Murda (1).mp3" },
    { title: "To The Moon", file: "wiggachain (3).mp3" },
    { title: "Rug Pull Blues", file: "Detection.mp3" },
    { title: "APY Dreams", file: "WiggaChain (5).mp3" },
    { title: "NFT Grind", file: "wiggachain (Remix).mp3" },
    { title: "DeFi Degens", file: "Wigganomics 101 (1).mp3" },
    { title: "Gas Fee Nightmare", file: "Wigganomics 101 (2).mp3" },
    { title: "Whale Alert", file: "Chain Gang Murda (2).mp3" }
  ];
  
  // === MUSIC CONTROL FUNCTIONS ===
  const playTrack = useCallback((index) => {
    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.pause();
    }
    
    // Load and play the new track
    backgroundMusicRef.current = new Audio(musicTracks[index].file);
    backgroundMusicRef.current.loop = true;
    backgroundMusicRef.current.volume = 0.3;
    
    if (isPlaying) {
      backgroundMusicRef.current.play().catch(e => 
        console.error('Error playing track:', e)
      );
    }
    
    setCurrentTrackIndex(index);
  }, [isPlaying]);
  
  const nextTrack = useCallback(() => {
    const nextIndex = (currentTrackIndex + 1) % musicTracks.length;
    playTrack(nextIndex);
  }, [currentTrackIndex, musicTracks.length, playTrack]);
  
  const prevTrack = useCallback(() => {
    const prevIndex = (currentTrackIndex - 1 + musicTracks.length) % musicTracks.length;
    playTrack(prevIndex);
  }, [currentTrackIndex, musicTracks.length, playTrack]);
  
  const togglePlayPause = useCallback(() => {
    if (backgroundMusicRef.current) {
      if (isPlaying) {
        backgroundMusicRef.current.pause();
      } else {
        backgroundMusicRef.current.play().catch(e => 
          console.error('Error playing track:', e)
        );
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);
  
  // === NAVIGATION FUNCTIONS ===
  const navigateToWiggaPaper = () => {
    setCurrentPage('wiggapaper');
    window.scrollTo(0, 0); // Scroll to top when navigating
  };
  
  const navigateToMain = () => {
    setCurrentPage('main');
    window.scrollTo(0, 0); // Scroll to top when navigating
  };
  
  // === MEMES LIST ===
  const memes = [
    "When someone asks where your NFT went: 'It's on the blockchain, trust me bro'",
    "My financial advisor: 'HODL'. My advisor on WiggaChain: 'WIGGLE'",
    "Normies: Buy low, sell high. Wiggaz: Buy high, sell higher, blame FED when broke",
    "What if I told you... layer 2 solutions let you trade without selling your house for gas fees",
    "Girls when they find out you're a WiggaChain validator: *slides into DMs*",
    "FBI: Delete all your crypto. WiggaChain: Delete the FBI",
    "WiggaChain dev meeting: 'Let's add another zero to token supply, for the culture'",
    "Other blockchains: We have smart contracts. WiggaChain: We have STREET contracts"
  ];
  
  // === INITIALIZE AUDIO ===
  useEffect(() => {
    // Initialize all audio elements
    try {
      // Scan initialization sound
      scanSoundRef.current = new Audio('algo.mp3');
      scanSoundRef.current.volume = 0.6;
      scanSoundRef.current.preload = 'auto';
      scanSoundRef.current.loop = true; // Loop during scanning
      
      // Access granted sound
      accessGrantedSoundRef.current = new Audio('Voicy_SEGA NIGGA.mp3');
      accessGrantedSoundRef.current.volume = 0.7;
      accessGrantedSoundRef.current.preload = 'auto';
      
      // Background music for main content (first track)
      backgroundMusicRef.current = new Audio(musicTracks[currentTrackIndex].file);
      backgroundMusicRef.current.loop = true;
      backgroundMusicRef.current.volume = 0.3;
      backgroundMusicRef.current.preload = 'auto';
      
      // Set up access granted sound ended event
      if (accessGrantedSoundRef.current) {
        accessGrantedSoundRef.current.onended = () => {
          // Start background music 2 seconds after access granted sound ends
          if (backgroundMusicTimerRef.current) {
            clearTimeout(backgroundMusicTimerRef.current);
          }
          
          backgroundMusicTimerRef.current = setTimeout(() => {
            if (backgroundMusicRef.current) {
              backgroundMusicRef.current.play().catch(e => 
                console.error('Error playing background music:', e)
              );
            }
          }, 2000);
        };
      }
    } catch (error) {
      console.error("Error initializing audio:", error);
    }
    
    // Clean up on unmount
    return () => {
      if (scanSoundRef.current) {
        scanSoundRef.current.pause();
        scanSoundRef.current = null;
      }
      if (accessGrantedSoundRef.current) {
        accessGrantedSoundRef.current.pause();
        accessGrantedSoundRef.current = null;
      }
      if (backgroundMusicRef.current) {
        backgroundMusicRef.current.pause();
        backgroundMusicRef.current = null;
      }
      if (backgroundMusicTimerRef.current) {
        clearTimeout(backgroundMusicTimerRef.current);
      }
    };
  }, [currentTrackIndex]);
  
  // === ANIMATION HELPERS ===
  // Easing function for smoother animation
  const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
  
  // === TYPEWRITER EFFECT ===
  const typeWriter = useCallback((text) => {
    // Clear any existing interval
    if (typewriterRef.current) {
      clearInterval(typewriterRef.current);
    }
    
    // Reset state
    setMemeText('');
    setIsTyping(true);
    
    let i = 0;
    typewriterRef.current = setInterval(() => {
      if (i < text.length) {
        setMemeText(prev => prev + text.charAt(i));
        i++;
      } else {
        clearInterval(typewriterRef.current);
        setIsTyping(false);
      }
    }, TYPING_SPEED);
  }, []);
  
  // === MEME SELECTION BASED ON PROGRESS ===
  useEffect(() => {
    // Show different memes at certain progress points
    if (scanActive) {
      if (scanProgress > 0 && scanProgress <= 10 && memeIndex !== 0) {
        setMemeIndex(0);
        typeWriter(memes[0]);
      } else if (scanProgress > 25 && scanProgress <= 30 && memeIndex !== 1) {
        setMemeIndex(1);
        typeWriter(memes[1]);
      } else if (scanProgress > 50 && scanProgress <= 55 && memeIndex !== 2) {
        setMemeIndex(2);
        typeWriter(memes[2]);
      } else if (scanProgress > 75 && scanProgress <= 80 && memeIndex !== 3) {
        setMemeIndex(3);
        typeWriter(memes[3]);
      } else if (scanProgress > 90 && scanProgress <= 95 && memeIndex !== 4) {
        setMemeIndex(4);
        typeWriter(memes[4]);
      }
    }
  }, [scanProgress, scanActive, memeIndex, memes, typeWriter]);
  
  // === SCAN ANIMATION ===
  const animateScan = useCallback((timestamp) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }
    
    const elapsed = timestamp - startTimeRef.current;
    const rawProgress = Math.min(elapsed / SCAN_DURATION, 1);
    const easedProgress = easeOutCubic(rawProgress) * 100;
    
    setScanProgress(easedProgress);
    
    if (easedProgress < 100) {
      // Continue animation
      animationRef.current = requestAnimationFrame(animateScan);
    } else {
      // Stop the algo sound when scan is complete
      if (scanSoundRef.current) {
        scanSoundRef.current.pause();
        scanSoundRef.current.currentTime = 0;
      }
      
      // Scan complete - play access granted sound
      if (accessGrantedSoundRef.current && !accessGrantedPlayed) {
        setAccessGrantedPlayed(true);
        accessGrantedSoundRef.current.play().catch(err => {
          console.error("Error playing access granted sound:", err);
          
          // If there's an error playing the access granted sound,
          // still start the background music after 2 seconds
          backgroundMusicTimerRef.current = setTimeout(() => {
            if (backgroundMusicRef.current) {
              backgroundMusicRef.current.play().catch(e => 
                console.error('Error playing background music:', e)
              );
            }
          }, 2000);
        });
      }
      
      // Wait before transition
      if (!completionTimerRef.current) {
        completionTimerRef.current = setTimeout(() => {
          setEntryComplete(true);
          
          // Direct transition to main content after entry screen
          setTimeout(() => {
            setShowMainContent(true);
          }, 1000);
        }, 3000);
      }
    }
  }, [accessGrantedPlayed]);
  
  // === START SCAN FUNCTION ===
  const startScan = useCallback(() => {
    // Reset all scan-related state
    setScanActive(true);
    setScanProgress(0);
    setMemeIndex(-1);
    setMemeText('');
    setAccessGrantedPlayed(false);
    startTimeRef.current = null;
    
    // Play scan initialization sound
    if (scanSoundRef.current) {
      scanSoundRef.current.currentTime = 0;
      scanSoundRef.current.play().catch(err => console.error("Error playing scan sound:", err));
    }
    
    // Clear any existing timers/animations
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    if (backgroundMusicTimerRef.current) {
      clearTimeout(backgroundMusicTimerRef.current);
      backgroundMusicTimerRef.current = null;
    }
    
    // Start the scan animation
    animationRef.current = requestAnimationFrame(animateScan);
  }, [animateScan]);
  
  // === HANDLE BACKGROUND VIDEO WHEN MAIN CONTENT SHOWS ===
  useEffect(() => {
    if (showMainContent && backgroundVideoRef.current) {
      // When transitioning to main content, play the background video with sound
      const playBackgroundVideo = async () => {
        try {
          await backgroundVideoRef.current.play();
          setBackgroundVideoLoaded(true);
        } catch (error) {
          console.error("Error playing background video:", error);
          // Video playback error is handled by the music already playing
        }
      };
      
      // Add slight delay to ensure smooth transition
      setTimeout(playBackgroundVideo, 500);
    }
  }, [showMainContent]);
  
  // === CLEANUP FUNCTION ===
  useEffect(() => {
    return () => {
      // Clean up all animations and timers
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (typewriterRef.current) {
        clearInterval(typewriterRef.current);
      }
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
      }
      if (backgroundMusicTimerRef.current) {
        clearTimeout(backgroundMusicTimerRef.current);
      }
    };
  }, []);
  
  // === ENTRY SCREEN COMPONENT ===
  const renderEntryScreen = () => {
    return (
      <div className="entry-screen" style={{
        opacity: entryComplete ? 0 : 1,
        transition: 'opacity 2s ease'
      }}>
        <div className="cyber-grid"></div>
        <div className="scan-container">
          <h1 className="site-title">WiggaChain</h1>
          <p className="subtitle">
            AI-Powered Layer 2 Solution with 
            <span className="highlight"> 100% Accuracy</span>
          </p>
          
          <div className="model-container">
            <div className="scan-rings"></div>
            <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
              <ambientLight intensity={0.5} />
              <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
              <pointLight position={[-10, -10, -10]} />
              <HeadModel scanProgress={scanProgress} scanActive={scanActive} />
              <OrbitControls 
                enableZoom={false} 
                enablePan={false} 
                autoRotate={!scanActive} 
                autoRotateSpeed={1} 
              />
            </Canvas>
          </div>
          
          {scanProgress === 0 ? (
            <button 
              className="scan-button" 
              onClick={startScan}
              disabled={scanActive && scanProgress > 0}
            >
              <span className="button-text">INITIATE WIGGA SCAN</span>
              <span className="button-glow"></span>
            </button>
          ) : (
            <div className="scan-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${scanProgress}%` }}
                />
              </div>
              <div className="progress-text-container">
                <p className="progress-text">
                  {scanProgress < 100 
                    ? `SCANNING: ${Math.floor(scanProgress)}%` 
                    : "SCAN COMPLETE - WELCOME WIGGA"}
                </p>
                {scanProgress >= 100 && (
                  <motion.p 
                    className="access-granted"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    ACCESS GRANTED
                  </motion.p>
                )}
              </div>
            </div>
          )}
          
          {scanActive && scanProgress > 0 && (
            <div className="meme-container">
              <motion.div 
                className="meme-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="meme-card-header">
                  <div className="meme-card-title">WIGGA WISDOM</div>
                  <div className="blinking-dot"></div>
                </div>
                <div className="meme-card-content">
                  <div className="meme-terminal">
                    <span className="terminal-prompt">wigga@chain:~$</span>
                    <span className="terminal-text">
                      {memeText || "Loading wisdom..."}
                    </span>
                    <span className={`cursor ${isTyping ? 'blink' : ''}`}>▋</span>
                  </div>
                </div>
                <div className="meme-card-footer">
                  <div className="status-text">SCANNING BLOCKCHAIN CULTURE...</div>
                  <div className="meme-number">MEME #{Math.floor(Math.random() * 1000 + 1)}</div>
                </div>
              </motion.div>
            </div>
          )}
          
          <div className="entry-footer">
            <div className="footer-text">QUANTUM-SECURED AUTHENTICATION</div>
            <div className="stats-line">
              <span>USERS: 69,420</span>
              <span>BLOCKS: 8,008,135</span>
              <span>UPTIME: 100%</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // === MAIN APP RENDER ===
  return (
    <div className="app">
      <AnimatePresence mode="wait">
        {!entryComplete && (
          <motion.div
            key="entry"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            style={{ width: '100%', height: '100vh' }}
          >
            {renderEntryScreen()}
          </motion.div>
        )}
        
        {showMainContent && currentPage === 'main' && (
          <motion.div
            key="main"
            className="main-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            {/* Background video for main content */}
            <div className="background-video-container">
              <video
                ref={backgroundVideoRef}
                className="background-video"
                src="/videos/background-loop.mp4"
                muted={false}
                loop
                playsInline
              />
            </div>
            
            <NavBar 
              audioRef={backgroundMusicRef.current}
              isPlaying={isPlaying}
              togglePlayPause={togglePlayPause}
              nextTrack={nextTrack}
              prevTrack={prevTrack}
              currentTrack={musicTracks[currentTrackIndex]}
              musicTracks={musicTracks}
              onTrackSelect={playTrack}
            />
            <Hero navigateToWiggaPaper={navigateToWiggaPaper} />
            <AboutSection />
            <HowToBuy />
            <Roadmap />
            <Footer />
          </motion.div>
        )}
        
        {showMainContent && currentPage === 'wiggapaper' && (
          <motion.div
            key="wiggapaper"
            className="wiggapaper-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <div className="back-button-container">
              <button onClick={navigateToMain} className="back-button">
                ← Back to Main
              </button>
            </div>
            <WiggaPaper />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default App;